// src/Inicio.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HeroSearchFilter, { FiltrosBusqueda } from './HeroSearchFilter'; 
import { ProductoConDetalles, Producto } from './types';
import { MOCK_RESENAS, Resena } from './data/mockData'; 
import { getFeaturedProducts } from './data/productos'; 
import AnimationWrapper from './AnimationWrapper'; 

interface InicioProps {
    onAddToCart?: (producto: Producto) => void;
}

// === ICONOS DE BENEFICIOS (SVG) ===
const IconoGarantia = () => (
    <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
);
const IconoRepuestos = () => (
    <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.5.305 1.077.5 1.724 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
);
const IconoTecnologia = () => (
    <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
);
const IconoFinanciamiento = () => (
    <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
);

const BENEFICIOS = [
    { titulo: "Garantía Extendida", descripcion: "Cobertura premium de 5 años o 150.000 KM en modelos seleccionados.", icono: <IconoGarantia /> },
    { titulo: "Stock de Repuestos", descripcion: "Disponibilidad inmediata de piezas originales para mantener tu inversión.", icono: <IconoRepuestos /> },
    { titulo: "Tecnología Inteligente", descripcion: "Asistencia a la conducción y seguridad activa de última generación.", icono: <IconoTecnologia /> },
    { titulo: "Sistema de Apartado", descripcion: "Planes a tu medida con aprobación rápida y tasas preferenciales.", icono: <IconoFinanciamiento /> }
];

const renderStars = (calificacion: number) => {
    return Array.from({ length: 5 }, (_, index) => (
        <svg key={index} className={`w-5 h-5 ${index < calificacion ? 'text-yellow-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.691-.921 1.99 0l1.247 3.834a1 1 0 00.95.691h4.049c.969 0 1.371 1.24.588 1.81l-3.264 2.375a1 1 0 00-.364 1.118l1.247 3.834c.3.921-.755 1.688-1.54 1.118l-3.264-2.375a1 1 0 00-1.176 0l-3.264 2.375c-.784.57-1.838-.197-1.539-1.118l1.247-3.834a1 1 0 00-.364-1.118L2.099 9.262c-.783-.57-.381-1.81.588-1.81h4.049a1 1 0 00.95-.691l1.247-3.834z" />
        </svg>
    ));
};

// Componente individual del Slider
const CarrouselItem: React.FC<{ imgSrc: string; altText: string; title: string; subtitle: string; onClick: () => void }> = ({ imgSrc, altText, title, subtitle, onClick }) => (
    <div className="relative h-[85vh] min-h-[600px] w-full">
        {/* Imagen de fondo con efecto de zoom lento */}
        <div className="absolute inset-0 overflow-hidden">
            <img 
                src={imgSrc} 
                alt={altText} 
                className="w-full h-full object-cover transition-transform duration-[10s] ease-linear transform scale-100 hover:scale-110" 
            />
        </div>
        
        {/* Gradiente oscuro para legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent flex items-center p-4 sm:p-12">
            <div className="text-left text-white max-w-3xl ml-0 md:ml-10 mt-10">
                <AnimationWrapper direction="right">
                    <p className="text-yellow-500 font-bold tracking-[0.3em] uppercase mb-4 text-sm md:text-base border-l-4 border-yellow-500 pl-4">
                        {subtitle}
                    </p>
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-tight tracking-tighter uppercase font-sans">
                        {title}
                    </h1>
                    <button
                        onClick={onClick}
                        className="group bg-white text-black font-extrabold py-4 px-10 hover:bg-yellow-500 hover:text-white transition duration-300 text-sm md:text-lg uppercase tracking-wider flex items-center gap-2"
                    >
                        Ver Catálogo
                        <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                    </button>
                </AnimationWrapper>
            </div>
        </div>
    </div>
);

const Inicio: React.FC<InicioProps> = () => {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleSearchFromHome = (filtros: FiltrosBusqueda) => {
        navigate('/catalogo', { state: { filtrosIniciales: filtros } });
    };

    // === CARRUSEL INTERCALADO (BESTUNE - OTROS - BESTUNE - OTROS) ===
    const carruselItems = [
        // 1. Bestune T55
        { 
            imgSrc: '/Imagen1.webp', 
            altText: 'Bestune T55', 
            title: 'Bestune T55', 
            subtitle: 'La Evolución del Diseño' 
        },
        // 2. Toyota Corolla Levin (INTERCALADO)
        { 
            imgSrc: '/Imagen4.webp', 
            altText: 'Toyota Corolla Levin', 
            title: 'Toyota Levin', 
            subtitle: 'Eficiencia y Elegancia' 
        },
        // 3. Bestune T77
        { 
            imgSrc: '/Imagen2.webp', 
            altText: 'Bestune T77', 
            title: 'Bestune T77', 
            subtitle: 'Tecnología que Inspira' 
        },
        // 4. Chevrolet Suburban (INTERCALADO)
        { 
            imgSrc: '/Imagen5.webp', 
            altText: 'Chevrolet Suburban', 
            title: 'Chevrolet Suburban', 
            subtitle: 'Poder para toda la familia' 
        },
        // 5. Bestune T99
        { 
            imgSrc: '/Imagen3.webp', 
            altText: 'Bestune T99', 
            title: 'Bestune T99', 
            subtitle: 'Lujo Sin Compromisos' 
        },
        // 6. Range Rover HSE (INTERCALADO)
        { 
            imgSrc: '/Imagen6.webp', 
            altText: 'Range Rover HSE', 
            title: 'Range Rover', 
            subtitle: 'Exclusividad Británica' 
        },
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % carruselItems.length);
        }, 6000); // 6 segundos por slide
        return () => clearInterval(interval);
    }, [carruselItems.length]);
    
    const productosDestacados = getFeaturedProducts(); 

    return (
        <>
            {/* === 1. CARRUSEL PRINCIPAL === */}
            <div className="relative overflow-hidden bg-black">
                <CarrouselItem 
                    {...carruselItems[currentIndex]} 
                    onClick={() => navigate('/catalogo')} 
                />
                
                {/* Indicadores (Puntos) */}
                <div className="absolute bottom-8 right-8 md:right-16 flex space-x-3 z-10">
                    {carruselItems.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            className={`h-1 transition-all duration-300 ${
                                currentIndex === index ? 'w-12 bg-yellow-500' : 'w-6 bg-gray-600 hover:bg-gray-400'
                            }`}
                            aria-label={`Ir a slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>

            {/* === 2. BARRA DE BÚSQUEDA Y FILTRO === */}
            <div className="bg-gray-100 -mt-20 relative z-20 py-8 px-4 sm:px-6 lg:px-8 shadow-xl">
                <AnimationWrapper direction='up'>
                    <HeroSearchFilter onSearchChange={handleSearchFromHome} />
                </AnimationWrapper>
            </div>

            {/* === 3. SECCIÓN DE PRODUCTOS DESTACADOS === */}
            <section id="destacados" className="bg-white pt-20 pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <AnimationWrapper direction='up'>
                            <span className="text-yellow-600 font-bold uppercase tracking-widest text-sm">Nuestro Inventario</span>
                            <h2 className="text-4xl md:text-5xl font-black text-black mt-2 uppercase">
                                Vehículos <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-700">Destacados</span>
                            </h2>
                            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-6"></div>
                        </AnimationWrapper>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {productosDestacados.map((producto: ProductoConDetalles, index) => (
                            <AnimationWrapper key={producto.id} direction='up' delay={index * 150 + 100}>
                                <Link 
                                    to={`/detalle/${producto.id}`}
                                    className="group block bg-white overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full flex flex-col"
                                >
                                    <div className="relative h-64 overflow-hidden">
                                        <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition z-10"></div>
                                        <img 
                                            src={producto.imgUrl} 
                                            alt={producto.nombre} 
                                            loading="lazy"
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                                        />
                                        <span className="absolute top-4 left-4 z-20 bg-black text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                                            {producto.tipoCarro || producto.tipo}
                                        </span>
                                        {producto.condicion === 'nuevo' && (
                                            <span className="absolute top-4 right-4 z-20 bg-yellow-500 text-black text-xs font-bold px-3 py-1 uppercase tracking-wider shadow-md">
                                                Nuevo
                                            </span>
                                        )}
                                    </div>

                                    <div className="p-6 flex flex-col flex-grow relative">
                                        <div className="mb-4">
                                            <h3 className="text-xl font-extrabold text-gray-900 group-hover:text-yellow-600 transition-colors uppercase leading-tight">
                                                {producto.nombre}
                                            </h3>
                                            <p className="text-gray-500 text-sm mt-1">{producto.año} • {producto.transmision?.split(' ')[0]}</p>
                                        </div>
                                        
                                        <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-gray-400 uppercase font-bold">Precio Contado</p>
                                                <p className="text-2xl font-black text-gray-900">${producto.precio.toLocaleString('es-ES')}</p>
                                            </div>
                                            <div className="w-10 h-10 bg-gray-100 flex items-center justify-center group-hover:bg-yellow-500 group-hover:text-white transition-colors">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </AnimationWrapper>
                        ))}
                    </div>

                    <div className="text-center mt-16">
                        <AnimationWrapper direction='up' delay={400}>
                            <Link
                                to="/catalogo"
                                className="inline-block border-2 border-black text-black font-bold py-4 px-12 hover:bg-black hover:text-white transition duration-300 text-sm uppercase tracking-widest"
                            >
                                Ver Catálogo Completo
                            </Link>
                        </AnimationWrapper>
                    </div>
                </div>
            </section>

            {/* === 4. SECCIÓN BENEFICIOS === */}
            <section className="bg-black py-24 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.webp')] opacity-10"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <AnimationWrapper direction='up'>
                        <div className="text-center mb-20">
                            <h2 className="text-4xl font-black mb-6 uppercase tracking-tight">Experiencia <span className="text-yellow-500">Premium</span></h2>
                            <p className="text-gray-400 max-w-2xl mx-auto text-lg font-light">
                                Cada detalle cuenta. Nos aseguramos de que tu experiencia de compra sea tan excepcional como tu nuevo vehículo.
                            </p>
                        </div>
                    </AnimationWrapper>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                        {BENEFICIOS.map((beneficio, index) => (
                            <AnimationWrapper key={index} direction='up' delay={index * 100 + 200}>
                                <div className="text-center group">
                                    <div className="inline-flex items-center justify-center p-4 bg-gray-900 mb-6 group-hover:scale-110 transition duration-300 border border-gray-800 group-hover:border-yellow-600 shadow-lg">
                                        {beneficio.icono}
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3 uppercase tracking-wide">
                                        {beneficio.titulo}
                                    </h3>
                                    <p className="text-gray-500 text-sm leading-relaxed px-4">
                                        {beneficio.descripcion}
                                    </p>
                                </div>
                            </AnimationWrapper>
                        ))}
                    </div>
                </div>
            </section>

            {/* === 5. SECCIÓN RESEÑAS === */}
            <div className="bg-gray-50 py-24 border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <AnimationWrapper direction='up'>
                        <h2 className="text-3xl font-black text-gray-900 text-center mb-16 uppercase tracking-tight">
                            Historias de Éxito
                        </h2>
                    </AnimationWrapper>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {MOCK_RESENAS.slice(0, 3).map((resena: Resena, index) => (
                            <AnimationWrapper key={resena.id} direction="up" delay={index * 150}>
                                <div className="bg-white p-10 shadow-xl border-t-4 border-yellow-500 hover:-translate-y-2 transition duration-300 relative">
                                    <div className="absolute -top-5 left-8 bg-yellow-500 text-white p-2 shadow-md">
                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21L14.017 18C14.017 16.8954 13.1216 16 12.017 16H9C9.00001 15 9.00001 14.3333 9.00001 14C9.00001 11 11 9 14 9V7C9.00001 7 7.00001 10 7.00001 14V21H14.017ZM21 21L21 18C21 16.8954 20.1046 16 19 16H15.983C15.983 15 15.983 14.3333 15.983 14C15.983 11 17.983 9 20.983 9V7C15.983 7 13.983 10 13.983 14V21H21Z"/></svg>
                                    </div>
                                    <div className="flex items-center mb-6 mt-2">
                                        {renderStars(resena.calificacion)}
                                    </div>

                                    <p className="text-gray-600 leading-relaxed mb-6 italic">
                                        "{resena.texto}"
                                    </p>

                                    <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                                        <div>
                                            <p className="font-bold text-gray-900 text-sm uppercase">{resena.nombre}</p>
                                            <p className="text-xs text-yellow-600 font-semibold mt-0.5">Propietario de {resena.modelo}</p>
                                        </div>
                                    </div>
                                </div>
                            </AnimationWrapper>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Inicio;